import { useCallback, useEffect, useRef, useState } from "react";
import { useFormBuilderEditorContext } from "../../formBuilderEditorContext";
import { TFormBuilderItem } from "../../model/item/formBuilderItem";
import { cloneDeep } from "lodash";
import { IFormBuilderRow } from "../../model/formBuilderRow";

interface IFormBuilderEntityViewRowMoveContext {
  type: "row";
  entity: IFormBuilderRow;
}

interface IFormBuilderEntityViewItemMoveContext {
  type: "item";
  entity: TFormBuilderItem;
  parentRow: IFormBuilderRow;
}

export type TFormBuilderEntityViewMoveContext = IFormBuilderEntityViewRowMoveContext | IFormBuilderEntityViewItemMoveContext;

export interface IUseFormBuilderEntityViewMoveArgs {
  context: TFormBuilderEntityViewMoveContext;
  moving: boolean;
}

interface IFormBuilderEntityViewMovePosition {
  x: number;
  y: number;
}

interface IFormBuilderEntityViewMoveDimensions {
  width: number;
  height: number;
}

interface IFormBuilderEntityViewMoveRefState {
  mouseOffset: IFormBuilderEntityViewMovePosition;
  moving: boolean;
}

interface IFormBuilderEntityViewMoveState {
  allowed: boolean;
  movePosition: IFormBuilderEntityViewMovePosition;
  moveDimensions: IFormBuilderEntityViewMoveDimensions;
}

type TFormBuilderHandleViewMovePointerDown = (e: React.PointerEvent<HTMLDivElement>) => void;

interface IUseFormBuilderEntityViewMove {
  viewMovePosition: IFormBuilderEntityViewMovePosition;
  viewMoveDimensions: IFormBuilderEntityViewMoveDimensions;
  handleViewMovePointerDown: TFormBuilderHandleViewMovePointerDown;
}

export function useFormBuilderEntityViewMove(args: IUseFormBuilderEntityViewMoveArgs): IUseFormBuilderEntityViewMove {
  const { formBuilderController, formBuilderEntityDomNodeRefMap, formRef } = useFormBuilderEditorContext();

  const moveRefState = useRef<IFormBuilderEntityViewMoveRefState>({
    mouseOffset: null,
    moving: false,
  });

  const [moveState, setMoveState] = useState<IFormBuilderEntityViewMoveState>({
    allowed: false,
    movePosition: null,
    moveDimensions: null,
  });

  const viewMovePosition: IFormBuilderEntityViewMovePosition = args.moving ? moveState.movePosition : null;
  const viewMoveDimensions: IFormBuilderEntityViewMoveDimensions = args.moving ? moveState.moveDimensions : null;

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const { mouseOffset, moving } = moveRefState.current;

    const ref = formBuilderEntityDomNodeRefMap.current.get(args.context.entity.key);
    const parentRef = formRef.current;

    const refBoundingRect = ref?.getBoundingClientRect();
    const parentBoundingRect = parentRef?.getBoundingClientRect();

    if (refBoundingRect == null || parentBoundingRect == null) {
      return;
    }

    const movePosition: IFormBuilderEntityViewMovePosition = {
      x: e.clientX - mouseOffset.x - parentBoundingRect.x,
      y: e.clientY - mouseOffset.y - parentBoundingRect.y,
    };

    setMoveState(prev => ({
      ...prev,
      movePosition,
      moveDimensions: prev.moveDimensions == null ? {
        width: refBoundingRect.width,
        height: refBoundingRect.height,
      } : prev.moveDimensions,
    }));

    if (!moving) {
      moveRefState.current.moving = true;

      ref.setPointerCapture(e.pointerId);

      formBuilderController.updateSelectedEntity({
        selectedEntityUpdates: {
          type: args.context.type,
          editingMode: {
            type: "move",
          },
        },
      });
    }
  }, [args.context, formBuilderController]);

  const handlePointerUp = useCallback((e: PointerEvent) => {
    const cleanup = () => {
      const ref = formBuilderEntityDomNodeRefMap.current.get(args.context.entity.key);

      ref?.releasePointerCapture(e.pointerId);

      moveRefState.current = {
        mouseOffset: null,
        moving: false,
      };

      setMoveState(prev => ({
        ...prev,
        allowed: false,
        movePosition: null,
        moveDimensions: null,
      }));
    };

    const abortMove = () => {
      formBuilderController.updateSelectedEntity({
        selectedEntityUpdates: {
          type: args.context.type,
          editingMode: {
            type: "none",
          },
        },
      });

      cleanup();
    };

    if (formBuilderController.overlayTarget == null) {
      abortMove();
      return;
    }

    if (args.context.type === "item") {
      const key = args.context.entity.key;
      const prevRow = args.context.parentRow;
      const prevRowKey = args.context.parentRow.key;

      const prevRowIndex = formBuilderController.form.children.findIndex(row => row.key === prevRowKey);
      const prevItemIndex = prevRow.children.findIndex(item => item.key === key);

      if (prevRowIndex === -1 || prevItemIndex === -1) {
        abortMove();
        return;
      }

      if (formBuilderController.overlayTarget.type === "row") {
        const targetRowIndex = formBuilderController.form.children.findIndex(row => row.key === formBuilderController.overlayTarget.key);

        if (targetRowIndex === -1) {
          abortMove();
          return;
        }

        const nextRowIndex = formBuilderController.overlayTarget.anchor === "above" ? targetRowIndex : targetRowIndex + 1;

        formBuilderController.moveItem({
          key,
          context: {
            type: "to-new-row",
            prevItemIndex,
            nextItemIndex: 0,
            prevRowIndex,
            nextRowIndex,
            prevRowKey,
            nextRowKey: crypto.randomUUID(),
            prevRow: cloneDeep(prevRow),
          },
        });

        cleanup();
        return;
      } else if (formBuilderController.overlayTarget.type === "item") {
        const targetRow = formBuilderController.form.children.find(row => row.children.some(item => item.key === formBuilderController.overlayTarget.key));
        const targetItemIndex = targetRow?.children.findIndex(item => item.key === formBuilderController.overlayTarget.key);
        const targetItem = targetRow?.children[targetItemIndex];
        
        if (targetRow == null || targetItemIndex == null || targetItemIndex === -1 || targetItem == null) {
          abortMove();
          return;
        }

        const nextItemIndex = formBuilderController.overlayTarget.anchor === "left" ? targetItemIndex : targetItemIndex + 1;

        if (targetRow.key === prevRowKey) {
          if (prevItemIndex === nextItemIndex) {
            abortMove();
            return;
          }

          formBuilderController.moveItem({
            key,
            context: {
              type: "within-row",
              prevItemIndex,
              nextItemIndex,
              rowKey: targetRow.key,
            },
          });

          cleanup();
          return;
        } else {
          formBuilderController.moveItem({
            key,
            context: {
              type: "to-other-row",
              origin: {
                type: "do-to-other-row",
                prevRow: cloneDeep(prevRow),
                prevRowIndex,
              },
              prevItemIndex,
              nextItemIndex,
              prevRowKey,
              nextRowKey: targetRow.key,
            },
          });

          cleanup();
          return;
        }
      } else {
        abortMove();
        return;
      }
    } else if (args.context.type === "row") {
      if (formBuilderController.overlayTarget.type === "item") {
        abortMove();
        return;
      }

      const key = args.context.entity.key;

      const prevIndex = formBuilderController.form.children.findIndex(row => row.key === key);
      const targetIndex = formBuilderController.form.children.findIndex(row => row.key === formBuilderController.overlayTarget.key);
      
      if (prevIndex === -1 || targetIndex === -1) {
        abortMove();
        return;
      }
      
      const nextIndex = formBuilderController.overlayTarget.anchor === "above" ? targetIndex : targetIndex + 1;

      if (prevIndex === nextIndex) {
        abortMove();
        return;
      }

      formBuilderController.moveRow({ key, prevIndex, nextIndex });

      cleanup();
      return;
    } else {
      abortMove();
      return;
    }
  }, [args.context, formBuilderController]);

  useEffect(() => {
    if (moveState.allowed) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [moveState.allowed, handlePointerMove, handlePointerUp]);

  const handleViewMovePointerDown: TFormBuilderHandleViewMovePointerDown = e => {
    const ref = formBuilderEntityDomNodeRefMap.current.get(args.context.entity.key);
    
    const boundingRect = ref?.getBoundingClientRect();

    if (boundingRect != null) {
      moveRefState.current.mouseOffset = {
        x: e.clientX - boundingRect.x,
        y: e.clientY - boundingRect.y,
      };

      moveRefState.current.moving = false;

      setMoveState(prev => ({
        ...prev,
        allowed: true,
      }));
    }
  };

  return {
    viewMovePosition,
    viewMoveDimensions,
    handleViewMovePointerDown,
  };
}